import { HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpInterceptorFn, HttpRequest } from "@angular/common/http";
import { inject } from "@angular/core";
import { firstValueFrom, Observable, throwError } from "rxjs";
import { catchError } from "rxjs/operators";
import { Cc7AuthRouteService, Cc7Error, MessageTypeEnum } from "@rinami/cc7-ng";
import { NetworkError, OfflineError, ServerError, UnauthorizedError } from "../errors/network-errors";
import { ApplicationContext } from "../../context/application-context.service";
import { LogoutWorker } from "../../../services/workers/logout-worker";

export interface ErrorInterceptorOptions {
  /**
   * Endpoints for which a security error must NOT trigger an automatic logout
   * (e.g. "/security/logout", otherwise a 401 from logout itself causes a logout loop).
   * Errors are still normalized and rethrown for these endpoints.
   *
   * Security errors are additionally never auto-logged-out while the router is on an auth route
   * (the login page or the SSO callback, see `Cc7AuthRouteService`), regardless of the endpoint.
   * The screens on those routes report the failure themselves (e.g. the SSO callback's confirmation dialog).
   */
  ignoredEndpoints?: string[];
}

export function errorInterceptor(options: ErrorInterceptorOptions = {}): HttpInterceptorFn {
  const ignoredEndpoints = options.ignoredEndpoints ?? [];

  return (req: HttpRequest<unknown>, next: HttpHandlerFn): Observable<HttpEvent<unknown>> => {
    const applicationContext = inject(ApplicationContext);
    const logoutWorker = inject(LogoutWorker);
    const authRoutes = inject(Cc7AuthRouteService);
    const requestUrl = req.url.startsWith("http") ? new URL(req.url).pathname : req.url;
    const isIgnored = ignoredEndpoints.some(endpoint => requestUrl.includes(endpoint));

    return next(req).pipe(
      catchError((err: unknown) => {
        // Body-level Cantara failures are already normalized by cc7-ng. Keep
        // them intact so consumers can use Cc7Error.title/description/code.
        if (err instanceof Cc7Error) {
          // If status is 0, it means the request failed to reach the server (e.g. DNS or network error)
          if (err.response?.status === 0 || applicationContext.deviceOffline()) {
            return throwError(() => new OfflineError(err.title || "No internet connection", err));
          }

          // security error -> logout the user and redirect to login page
          // (skipped for ignored endpoints, e.g. a 401 from /security/logout would cause a logout loop,
          // and while on an auth route, where the login / SSO callback screen reports the failure itself)
          if (!isIgnored && !authRoutes.isOnAuthRoute() && (err.messageType === MessageTypeEnum.NUMBER_30 || err.response?.status === 401)) {
            void firstValueFrom(logoutWorker.logout());
          }

          return throwError(() => err);
        }

        if (err instanceof HttpErrorResponse) {
          // If navigator says we are offline, or status is 0 (which often means blocked/no connection)
          if (applicationContext.deviceOffline() || err.status === 0) {
            return throwError(() => new OfflineError("No internet connection", err));
          }

          if (err.status === 401 || err.status === 403) {
            return throwError(() => new UnauthorizedError(err.message, err.status, err));
          }
          if (err.status >= 500) {
            return throwError(() => new ServerError(err.message, err.status, err));
          }
          return throwError(() => new NetworkError(err.message, err.status, err));
        }
        return throwError(() => new NetworkError("Unknown network error", undefined, err));
      })
    );
  };
}
